import React, { useEffect, useState } from 'react';
import { Modal, Input, InputNumber, Select, Typography, message } from 'antd';
import { DEFAULT_PASS_MARK_PERCENT, DEFAULT_MAX_ATTEMPTS } from '../../utils/trainingModel';
import { useApiRequest } from '../../hooks/useApiRequest';

const { Text } = Typography;

/**
 * Create / edit a training course (draft only — the API 409s an edit of a published course;
 * a published course is changed by forking a new version instead, Commandment VII).
 * POST/PATCH /projects/:id/training/courses — Advisor / Lead Advisor only (server-enforced).
 *
 * ISO/IEC 27002:2022 A.6.3 — control refs default-suggest A.6.3.
 */
const CourseFormModal = ({ open, projectId, course, onClose, onSaved }) => {
    const { request } = useApiRequest();
    const editing = !!course;
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [controlRefs, setControlRefs] = useState(['A.6.3']);
    const [targetAudience, setTargetAudience] = useState('');
    const [passMark, setPassMark] = useState(DEFAULT_PASS_MARK_PERCENT);
    const [maxAttempts, setMaxAttempts] = useState(DEFAULT_MAX_ATTEMPTS);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setTitle(course?.title || '');
        setDescription(course?.description || '');
        setControlRefs(course ? course.controlRefs || [] : ['A.6.3']);
        setTargetAudience(course?.targetAudience || '');
        setPassMark(typeof course?.passMarkPercent === 'number' ? course.passMarkPercent : DEFAULT_PASS_MARK_PERCENT);
        setMaxAttempts(typeof course?.maxAttempts === 'number' ? course.maxAttempts : DEFAULT_MAX_ATTEMPTS);
        setError(null);
    }, [open, course]);

    const handleOk = async () => {
        if (!title.trim()) return setError('Title is required.');
        if (!description.trim()) return setError('Description is required.');
        if (!Number.isInteger(passMark) || passMark < 1 || passMark > 100) return setError('Pass mark must be a whole number from 1 to 100.');
        if (!Number.isInteger(maxAttempts) || maxAttempts < 1 || maxAttempts > 20) return setError('Maximum attempts must be a whole number from 1 to 20.');
        setSaving(true);
        setError(null);
        try {
            const body = { title: title.trim(), description: description.trim(), controlRefs, targetAudience: targetAudience.trim(), passMarkPercent: passMark, maxAttempts };
            const path = editing
                ? `/projects/${projectId}/training/courses/${course.id}`
                : `/projects/${projectId}/training/courses`;
            const { data } = await request(editing ? 'PATCH' : 'POST', path, { body });
            message.success(editing ? 'Course updated.' : 'Draft course created.');
            onSaved(data.course);
        } catch (err) {
            setError(err.message || 'Could not save the course.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            title={editing ? 'Edit course details' : 'New training course'}
            okText={editing ? 'Save changes' : 'Create draft'}
            confirmLoading={saving}
            onOk={handleOk}
            onCancel={onClose}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                    <Text strong>Title</Text>
                    <Input aria-label="Course title" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Information security awareness for all staff" />
                </div>
                <div>
                    <Text strong>Description</Text>
                    <Input.TextArea aria-label="Course description" rows={3} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
                <div>
                    <Text strong>Control references</Text>
                    <Select
                        aria-label="Control references"
                        mode="tags"
                        style={{ width: '100%' }}
                        value={controlRefs}
                        onChange={setControlRefs}
                        tokenSeparators={[',']}
                        placeholder="A.6.3, DPDPA §8(4)"
                    />
                </div>
                <div>
                    <Text strong>Target audience</Text>
                    <Input aria-label="Target audience" maxLength={1000} value={targetAudience} onChange={(e) => setTargetAudience(e.target.value)} placeholder="e.g. Engineering, HR, all employees" />
                </div>
                <div>
                    <Text strong>Quiz pass mark (%)</Text>
                    <div>
                        <InputNumber aria-label="Pass mark percent" min={1} max={100} precision={0} value={passMark} onChange={setPassMark} />
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>A learner must reach this to complete the course and receive a certificate.</Text>
                </div>
                <div>
                    <Text strong>Maximum quiz attempts</Text>
                    <div>
                        <InputNumber aria-label="Maximum attempts" min={1} max={20} precision={0} value={maxAttempts} onChange={setMaxAttempts} />
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>After this many failed attempts a learner is locked out until the Lead Advisor resets their attempts.</Text>
                </div>
                {error && <Text type="danger" role="alert">{error}</Text>}
            </div>
        </Modal>
    );
};

export default CourseFormModal;
